import { normalizeArtifactRef } from 'flare-chat-core/contracts/context/artifact-ref';
import { isContextArtifactAdmissible } from 'flare-chat-core/contracts/canvas/artifactCanvasAdmissibility';
import { normalizeArtifactStructureState } from './artifactStructureState.js';

const object = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const array = (value) => Array.isArray(value) ? value : [];
const identity = ['artifact_id', 'artifact_version_id', 'artifact_type', 'content_digest'];
const sameReference = (left, right) => identity.every((key) => left[key] === right[key])
  && ['tenant_id', 'project_id'].every((key) => left.scope?.[key] === right.scope?.[key]);

/** Null/empty canonical tabs are authoritative and never resurrect legacy tabs. */
export const selectAuthoritativeCanvasTabs = (projection) => {
  const tabs = array(object(projection).tabs);
  const valid = tabs.every((tab) => typeof tab?.key === 'string' && tab.key
    && typeof tab?.render_surface === 'string' && tab.render_surface);
  if (!valid || new Set(tabs.map((tab) => tab.key)).size !== tabs.length) return [];
  return tabs.map((tab) => ({ ...tab }));
};
export const selectCanvasArtifactTab = ({ artifactTabProjection, canvasActiveTabKey } = {}) => {
  const projection = object(artifactTabProjection);
  const key = String(canvasActiveTabKey ?? '').trim() || projection.active_tab;
  const tab = selectAuthoritativeCanvasTabs(projection).find((entry) => entry.key === key);
  if (tab?.metadata?.artifact_ref && tab.artifact_type && tab.artifact_type !== tab.metadata.artifact_ref.artifact_type) return null;
  if (!tab || (key === projection.active_tab && projection.active_artifact_id
    && projection.active_artifact_id !== tab.artifact_id)) return null;
  return tab;
};

/** Bind one immutable row to the exact backend-selected tab, including its digest and scope. */
export const selectBoundArtifactProjection = (sessionContext, canvasActiveTabKey) => {
  const tab = selectCanvasArtifactTab({ artifactTabProjection: sessionContext.artifact_tab_projection, canvasActiveTabKey });
  if (!tab || tab.readiness !== 'ready') return null;
  let binding;
  try { binding = normalizeArtifactRef(tab.metadata?.artifact_ref); } catch { return null; }
  const rows = array(sessionContext.artifact_context).filter((item) => sameReference(object(item?.artifact_ref), binding)
    && item.artifact_id === binding.artifact_id && item.artifact_type === binding.artifact_type
    && item.accepted !== false && !['blocked', 'failed', 'rejected', 'not_admitted'].includes(item.status)
    && isContextArtifactAdmissible(item));
  if (rows.length !== 1) return null;
  const row = rows[0];
  const semantic = object(tab.metadata?.semantic_projection);
  const semanticProjection = sameReference(object(semantic.artifact_ref), binding) ? semantic : null;
  const presentation = object(tab.metadata?.document_presentation);
  const documentPresentation = sameReference(object(presentation.artifact_ref), binding) ? presentation : null;
  const revision = { ...row, active_tab: tab.key, render_surface: tab.render_surface,
    artifact_ref: binding, artifact_version_id: binding.artifact_version_id, revision_id: binding.artifact_version_id,
    semantic_projection: semanticProjection, document_presentation: documentPresentation };
  return { content: typeof row.content === 'string' ? row.content : '', artifact_ref: binding,
    artifact_document: { ...normalizeArtifactStructureState(revision), kind: 'ArtifactDocument', latest_revision: revision },
    workspace_capabilities: object(row.workspace_capabilities), versions: [revision],
    collected: array(row.collected), candidate_fields: array(row.candidate_fields), missing: array(row.missing),
    has_intake_canvas_state: true };
};
